import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MagnifyingGlassIcon, UsersIcon } from "@heroicons/react/20/solid";
import { FaSpinner } from "react-icons/fa";
import { debounce } from "lodash";
import moment from "moment";
import { useGroupChatContext } from "../../context/groupchatContext";
import ImgHolder from "../../components/imgHolder";
import HistoryMessage from "./historyMessage";
import {
  useGetPatientChatHistoryQuery,
  useLazySearchInactivePatientsChatHistoryQuery,
} from "../../services/chatService";

const getPatientName = (patient) =>
  [patient?.fname, patient?.lname].filter(Boolean).join(" ") ||
  patient?.full_name ||
  patient?.name ||
  "";

const getPatientLocationId = (patient) => patient?.location_id || patient?.location?.id;

const ChatHistoryEmptyState = ({
  title = "No Patient Selected",
  description = "Use the search bar above to find a patient and view their chat history.",
}) => (
  <div className="relative flex w-full max-w-[450px] flex-col items-center overflow-hidden rounded-3xl border border-white bg-gradient-to-br from-white via-white to-cyan-50/70 px-10 py-9 text-center shadow-2xl shadow-cyan-900/15 ring-1 ring-cyan-100">
    <div className="absolute left-0 top-0 h-1.5 w-full bg-gradient-to-r from-bgGreen via-cyan-400 to-blue-400" aria-hidden="true" />
    <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-500 shadow-lg shadow-cyan-500/25 ring-8 ring-white" aria-hidden="true">
      <MagnifyingGlassIcon className="h-7 w-7 text-white" />
    </div>
    <h2 className="relative text-2xl font-bold text-slate-900">{title}</h2>
    <p className="relative mt-3 max-w-sm text-sm font-medium leading-6 text-slate-500">{description}</p>
    <span className="mt-5 rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-semibold text-slate-600 shadow-sm">
      Search a patient to get started
    </span>
  </div>
);

const ChatHistoryPage = () => {
  const groupChatContext = useGroupChatContext();
  const { authData, setShowChatHistory } = useMemo(() => ({
    authData: groupChatContext.authData,
    setShowChatHistory: groupChatContext.setShowChatHistory,
  }));

  const [query, setQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientSelectionKey, setPatientSelectionKey] = useState(0);
  const [pagination, setPagination] = useState({ limit: 50, before_id: "", after_id: "" });
  const [historyMessages, setHistoryMessages] = useState({ data: [], meta: {} });
  const [showUrgentOnly, setShowUrgentOnly] = useState(false);
  const [wantsToSearchMessages, setWantsToSearchMessages] = useState(false);
  const [messageSearchInput, setMessageSearchInput] = useState("");
  const [messageKeyword, setMessageKeyword] = useState("");
  const [searchDropdownPosition, setSearchDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const searchContainerRef = useRef(null);
  const searchDropdownRef = useRef(null);
  const scrollRef = useRef(null);
  const skipNextSearchRef = useRef(false);

  const [searchPatients, { isFetching: isSearchingPatients }] = useLazySearchInactivePatientsChatHistoryQuery();
  const selectedPatientPid = selectedPatient?.pid;
  const selectedLocationId = getPatientLocationId(selectedPatient);

  const {
    currentData: patientChatHistory,
    isFetching: isFetchingMessages,
    originalArgs: patientChatHistoryArgs,
  } = useGetPatientChatHistoryQuery(
    {
      pid: selectedPatientPid,
      location_id: selectedLocationId,
      limit: pagination.limit,
      before_id: pagination.before_id,
      after_id: pagination.after_id,
      urgent: showUrgentOnly ? 1 : 0,
      keyword: messageKeyword || undefined,
      request_key: patientSelectionKey,
    },
    { skip: !selectedPatientPid || !selectedLocationId, refetchOnMountOrArgChange: true }
  );

  const resetHistoryMessages = useCallback(() => {
    setPagination({ limit: 50, before_id: "", after_id: "" });
    setHistoryMessages({ data: [], meta: {} });
  }, []);

  const debouncedSearchPatients = useMemo(() => debounce(async (keyword) => {
    if (!keyword || keyword.length < 3) {
      setPatients([]);
      setIsSearchOpen(false);
      return;
    }

    const response = await searchPatients({ keyword });
    setPatients(response?.data || []);
    setIsSearchOpen(true);
  }, 500), [searchPatients]);

  useEffect(() => {
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      return undefined;
    }

    debouncedSearchPatients(query);
    return () => debouncedSearchPatients.cancel();
  }, [query, debouncedSearchPatients]);

  const debouncedMessageSearch = useMemo(() => debounce((keyword) => {
    resetHistoryMessages();
    setMessageKeyword(keyword.trim());
  }, 500), [resetHistoryMessages]);

  useEffect(() => {
    if (messageSearchInput.trim() === messageKeyword) {
      return undefined;
    }

    debouncedMessageSearch(messageSearchInput);
    return () => debouncedMessageSearch.cancel();
  }, [messageSearchInput, messageKeyword, debouncedMessageSearch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target) &&
        !searchDropdownRef.current?.contains(event.target)
      ) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isSearchOpen || !searchContainerRef.current) {
      return undefined;
    }

    const updateDropdownPosition = () => {
      const rect = searchContainerRef.current.getBoundingClientRect();
      setSearchDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    };

    updateDropdownPosition();
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", updateDropdownPosition, true);

    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", updateDropdownPosition, true);
    };
  }, [isSearchOpen]);

  useEffect(() => {
    if (!patientChatHistory) {
      return;
    }

    const loadedBeforeId = patientChatHistoryArgs?.before_id;
    setHistoryMessages((prevMessages) => ({
      data: loadedBeforeId
        ? [...(prevMessages?.data || []), ...(patientChatHistory?.data || [])]
        : patientChatHistory?.data || [],
      meta: patientChatHistory?.meta || {},
    }));
  }, [
    patientChatHistory,
    patientChatHistoryArgs?.before_id,
    patientChatHistoryArgs?.keyword,
    patientChatHistoryArgs?.urgent,
    patientChatHistoryArgs?.pid,
    patientChatHistoryArgs?.location_id,
  ]);

  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    setPatientSelectionKey((prevKey) => prevKey + 1);
    skipNextSearchRef.current = true;
    setQuery(getPatientName(patient));
    setPatients([]);
    setIsSearchOpen(false);
    resetHistoryMessages();
    setShowUrgentOnly(false);
    setWantsToSearchMessages(false);
    setMessageSearchInput("");
    setMessageKeyword("");
    setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 10);
  };

  const fetchMoreMessages = () => {
    const messages = historyMessages?.data || [];
    const lastMessage = messages[messages.length - 1];
    if (!lastMessage?.id) {
      return;
    }

    setPagination((prevPagination) => ({
      ...prevPagination,
      after_id: "",
      before_id: lastMessage.id,
    }));
  };

  const hasMoreMessages = Number(historyMessages?.meta?.count || 0) > (historyMessages?.data || []).length;
  const selectedPatientName = getPatientName(selectedPatient);
  const selectedPatientDob = selectedPatient?.DOB ? moment(selectedPatient.DOB) : null;
  const selectedPatientDobText = selectedPatientDob?.isValid() ? selectedPatientDob.format("MM/DD/YYYY") : "";
  const selectedPatientDemographics = [
    selectedPatient?.sex,
    [selectedPatientDobText, selectedPatient?.age ? `(${selectedPatient.age} Y)` : ""].filter(Boolean).join(" "),
  ].filter(Boolean).join(", ");

  return (
    <div className="flex h-screen min-h-screen flex-col overflow-hidden bg-blue-50">
      <header
        className={`relative z-[999] flex items-center justify-between gap-4 shrink-0 overflow-visible ${
          authData?.is_provider ? "bg-ethizo_primary" : "bg-bg-primary"
        } flex-row px-4 sm:px-6 py-2 min-h-16 text-white shadow-sm`}
      >
        <div className="flex min-w-0 items-center gap-3">
          <h1 className="shrink-0 text-lg sm:text-xl font-semibold truncate">Chat History</h1>
          <div ref={searchContainerRef} className="relative z-[999] w-[280px] max-w-[45vw] text-gray-900">
            <div className="relative rounded-lg bg-white shadow ring-1 ring-black ring-opacity-5">
              {isSearchingPatients ? (
                <FaSpinner className="animate-spin pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" size={16} />
              ) : (
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" aria-hidden="true" />
              )}
              <input
                className="h-9 w-full rounded-lg border-0 bg-transparent pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:ring-0"
                placeholder="Search Patient"
                autoComplete="off"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onFocus={() => patients.length && setIsSearchOpen(true)}
              />
            </div>

            {isSearchOpen ? createPortal((
              <div
                ref={searchDropdownRef}
                className="fixed z-[9999] max-h-[70vh] overflow-y-auto rounded-lg bg-white p-2 text-gray-900 shadow-2xl ring-1 ring-black ring-opacity-5"
                style={{
                  top: searchDropdownPosition.top,
                  left: searchDropdownPosition.left,
                  width: searchDropdownPosition.width,
                }}
              >
                {patients.length ? patients.map((patient) => {
                  const patientName = getPatientName(patient);
                  const patientDob = patient?.DOB ? moment(patient.DOB) : null;
                  const patientDobText = patientDob?.isValid() ? patientDob.format("MM/DD/YYYY") : "";
                  return (
                    <button
                      key={`${patient?.pid}-${getPatientLocationId(patient) || ""}`}
                      type="button"
                      className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left transition-colors hover:bg-light-gray-f5"
                      onClick={() => handleSelectPatient(patient)}
                    >
                      <div className="h-[42px] w-[42px] shrink-0 rounded-full">
                        <ImgHolder uuid={patient?.file ? patient.file : patient?.sex} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-gray-900" title={patientName}>{patientName}</p>
                        <p className="text-xs font-medium text-gray-500 capitalize">
                          {patient?.sex}{patientDobText ? `, ${patientDobText}` : ""}{patient?.age ? ` (${patient.age} Y)` : ""}
                        </p>
                        {patient?.mrn ? <p className="text-xs font-medium text-gray-500">MRN: {patient.mrn}</p> : null}
                        {patient?.location?.name ? <p className="truncate text-xs font-normal text-slate-400" title={patient.location.name}>{patient.location.name}</p> : null}
                      </div>
                    </button>
                  );
                }) : (
                  <div className="px-6 py-10 text-center text-sm">
                    <UsersIcon className="mx-auto h-6 w-6 text-gray-400" aria-hidden="true" />
                    <p className="mt-4 font-semibold text-gray-900">No patient found</p>
                    <p className="mt-2 text-gray-500">We couldn't find anything with that term. Please try again.</p>
                  </div>
                )}
              </div>
            ), document.body) : null}
          </div>
        </div>
        <button
          onClick={() => setShowChatHistory(false)}
          title="Go Back to Home"
          aria-label="Go Back to Home"
          className="font-medium py-2 px-3 sm:px-4 rounded-lg hover:bg-white/15 active:scale-[0.98] transition-all text-sm sm:text-base shrink-0"
        >
          Back to Home
        </button>
      </header>

      <main className="relative flex-1 min-h-0 flex flex-col bg-blue-50">
        {selectedPatient ? (
          <>
            <div className="shrink-0 border-b border-gray-200 bg-white px-3 py-2">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="h-[42px] w-[42px] shrink-0 rounded-full">
                    <ImgHolder uuid={selectedPatient?.file ? selectedPatient.file : selectedPatient?.sex} />
                  </div>
                  <div className="min-w-0 text-left">
                    <p className="truncate text-base font-bold text-black-300" title={selectedPatientName}>{selectedPatientName}</p>
                    {selectedPatient?.mrn ? <p className="text-sm text-black-300">MRN: {selectedPatient.mrn}</p> : null}
                    {selectedPatientDemographics ? <p className="text-xs text-black-300 capitalize">{selectedPatientDemographics}</p> : null}
                    {selectedPatient?.location?.name ? <p className="truncate text-xs font-normal text-slate-400" title={selectedPatient.location.name}>{selectedPatient.location.name}</p> : null}
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-3">
                  {wantsToSearchMessages ? (
                    <div className="relative w-full md:w-[260px]">
                      <input
                        type="text"
                        value={messageSearchInput}
                        onChange={(event) => setMessageSearchInput(event.target.value)}
                        placeholder="Type here to Search Messages"
                        className="h-9 w-full rounded-md border-2 px-2 pr-8 text-sm focus:outline-none"
                      />
                      <button
                        type="button"
                        className="absolute right-2 top-2 text-gray-400"
                        onClick={() => {
                          setWantsToSearchMessages(false);
                          debouncedMessageSearch.cancel();
                          resetHistoryMessages();
                          setMessageSearchInput("");
                          setMessageKeyword("");
                        }}
                      >
                        X
                      </button>
                    </div>
                  ) : (
                    <button type="button" title="Search messages" onClick={() => setWantsToSearchMessages(true)}>
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </button>
                  )}
                  <label className="flex items-center gap-2 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={showUrgentOnly}
                      onChange={(event) => {
                        resetHistoryMessages();
                        setShowUrgentOnly(event.target.checked);
                      }}
                    />
                    <span>Show Urgent</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="relative z-0 flex-1 overflow-hidden">
              {isFetchingMessages ? (
                <div className="absolute inset-0 z-10 flex items-center justify-center">
                  <FaSpinner className="animate-spin text-green-300-500 ml-1" size={40} />
                </div>
              ) : null}
              <div id="chatHistoryScrollableDiv" ref={scrollRef} className="flex h-full flex-col-reverse overflow-y-auto border-r p-3">
                {historyMessages.data.length ? (
                  historyMessages.data.map((message, index) => (
                    <HistoryMessage key={(message?.id || "history-message") + "-" + index} msg={message} authData={authData} />
                  ))
                ) : !isFetchingMessages ? (
                  <div className="flex h-full items-center justify-center pb-24">
                    <ChatHistoryEmptyState
                      title="No Chat History Found"
                      description="This patient does not have archived chat messages matching the current filters."
                    />
                  </div>
                ) : null}
                {hasMoreMessages ? (
                  <button
                    type="button"
                    className="mx-auto my-3 rounded border border-bgGreen px-3 py-1 text-bgGreen hover:bg-bgGreen hover:text-white"
                    onClick={fetchMoreMessages}
                    disabled={isFetchingMessages}
                  >
                    Load older messages
                  </button>
                ) : null}
              </div>
            </div>
          </>
        ) : (
          <div className="relative z-0 flex flex-1 items-center justify-center px-4 pb-28">
            <ChatHistoryEmptyState />
          </div>
        )}
      </main>
    </div>
  );
};

export default ChatHistoryPage;
